<template>
  <AuthLayout>
    <template #header>
      <h1 class="text-[32px] font-semibold -mb-1.5">Create a Password</h1>
    </template>

    <div class="w-full">
      <!-- Form inputs -->
      <!-- Wrapped in a <form> so Safari/iOS treats the two fields as a
           password + confirmation pair and auto-fills BOTH with the
           generated strong password. -->
      <form class="flex flex-col gap-3 mb-8 w-full" @submit.prevent="handleNextStep">
        <div>
          <BaseInput
            v-model="authStore.signup_fields.password"
            label="Password"
            place_holder="Enter your password"
            field_type="string"
            variant="secondary"
            is_secure_field
            is_required
            autocomplete="new-password"
            :password_rules="passwordRules"
            :error_message="passwordErrorMessage"
            @focus="typingPassword = true"
            @keydown.space.prevent
            @keydown.enter="handleNextStep"
          />

          <p v-if="typingPassword" class="mt-3 ml-3 font-inter text-sm flex items-center flex-wrap">
            <Icon
              v-if="!allCriteriaMet"
              name="Asterisk"
              class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error shrink-0"
            />
            <Icon v-else name="Check" class="mr-2 h-[14px] w-[14px] stroke-success shrink-0" />
            <span
              class="whitespace-nowrap font-inter"
              :class="getCriteriaClass(passwordCriteria.length)"
            >
              12 Characters
            </span>
            <span class="text-strong mx-1">&middot;</span>
            <span
              class="whitespace-nowrap font-inter"
              :class="getCriteriaClass(passwordCriteria.uppercase)"
            >
              1 Uppercase Letter
            </span>
            <span class="text-strong mx-1">&middot;</span>
            <span
              class="whitespace-nowrap font-inter"
              :class="getCriteriaClass(passwordCriteria.number)"
            >
              1 Number
            </span>
          </p>
        </div>

        <div>
          <BaseInput
            v-model="authStore.signup_fields.confirm_password"
            label="Confirm Password"
            place_holder="Enter your password again"
            field_type="string"
            variant="secondary"
            is_secure_field
            is_required
            autocomplete="new-password"
            :password_rules="passwordRules"
            :error_message="confirmPasswordErrorMessage"
            @keydown.space.prevent
            @keydown.enter="handleNextStep"
          />
        </div>
      </form>
      <!-- CTA Buttons -->

      <Button
        @click="handleNextStep"
        size="md"
        variant="primary-grey"
        class="w-full max-lg:hidden"
        @keydown.enter="handleNextStep"
      >
        <template #label>Next</template>
      </Button>
    </div>

    <template #cta>
      <Button @click="handleNextStep" size="md" variant="primary-grey" class="w-full">
        <template #label>Next</template>
      </Button>
    </template>
  </AuthLayout>
</template>

<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/stores";
import { BaseInput, Button, Icon } from "@/components";
import AuthLayout from "./AuthLayout.vue";

const router = useRouter();
const authStore = useAuthStore();

// Password-generation hint for browsers/keychains that honor `passwordrules`
// (Safari/Keychain, 1Password). Chrome/Google Password Manager ignores it.
// We no longer require a special character — Chrome's generator won't include
// one, and forcing it created signup friction. Length + upper/lower/digit is
// aligned with NIST SP 800-63B (favor length over composition rules).

// We really need a single source of truth for password rules that validation also references, so we don't have to maintain this in multiple places.
const passwordRules = "minlength: 12; required: lower; required: upper; required: digit;";

const typingPassword = ref(false);
const passwordsMatch = ref(true);
const attemptedSubmit = ref(false);
const passwordCriteria = ref({
  length: false,
  uppercase: false,
  number: false,
});

onMounted(() => {
  window.scrollTo(0, 0);
  if (!authStore.signup_fields.email) {
    router.push("/sign-up");
  }
  validatePassword();
});

const allCriteriaMet = computed(() => {
  return (
    passwordCriteria.value.length &&
    passwordCriteria.value.uppercase &&
    passwordCriteria.value.number
  );
});

watch(
  () => authStore.signup_fields.password,
  () => {
    validatePassword();
    matchPasswords();
  }
);

watch(
  () => authStore.signup_fields.confirm_password,
  () => matchPasswords()
);

const passwordErrorMessage = computed(() => {
  if (attemptedSubmit.value && !authStore.signup_fields.password) {
    return "Please enter a value for Password.";
  }
  return "";
});

const confirmPasswordErrorMessage = computed(() => {
  if (attemptedSubmit.value && !authStore.signup_fields.confirm_password) {
    return "Please confirm your password.";
  }
  if (!passwordsMatch.value && authStore.signup_fields.confirm_password) {
    return "Passwords do not match.";
  }
  return "";
});

const isFormValid = computed(() => {
  return (
    passwordCriteria.value.length &&
    passwordCriteria.value.uppercase &&
    passwordCriteria.value.number &&
    passwordsMatch.value &&
    authStore.signup_fields.password &&
    authStore.signup_fields.confirm_password
  );
});

function handleNextStep() {
  attemptedSubmit.value = true;
  if (!isFormValid.value) {
    return;
  }
  authStore.active_signup_step = 4;
  router.push("/sign-up/acknowledgements");
}

function validatePassword() {
  const pwd = authStore.signup_fields.password;
  passwordCriteria.value.length = pwd.length >= 12;
  passwordCriteria.value.uppercase = /[A-Z]/.test(pwd);
  passwordCriteria.value.number = /\d/.test(pwd);
}

function getCriteriaClass(isValid) {
  return isValid ? "text-success" : "text-error";
}

function matchPasswords() {
  if (authStore.signup_fields.password === authStore.signup_fields.confirm_password) {
    passwordsMatch.value = true;
  } else {
    passwordsMatch.value = false;
  }
}
</script>
